import { useEffect, useState } from 'react';
import { deviceApi } from '../services/deviceApi';
import { pickDropdownId } from '../utils/deviceVersionUtils';

/**
 * Device assignment dropdowns ONLY:
 * - GET /super-admin/hardware/dropdown
 * - GET /super-admin/software/compatible/:hardware_id
 */
export function useDeviceVersionOptions(hardwareId) {
  const [hardwareList, setHardwareList] = useState([]);
  const [displaySoftware, setDisplaySoftware] = useState([]);
  const [deviceSoftware, setDeviceSoftware] = useState([]);
  const [hardwareLoading, setHardwareLoading] = useState(true);
  const [softwareLoading, setSoftwareLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHardwareLoading(true);
    deviceApi
      .getHardwareDropdown('device')
      .then((list) => { if (!cancelled) setHardwareList(list); })
      .catch(() => { if (!cancelled) setHardwareList([]); })
      .finally(() => { if (!cancelled) setHardwareLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!hardwareId) {
      setDisplaySoftware([]);
      setDeviceSoftware([]);
      return undefined;
    }

    let cancelled = false;
    setSoftwareLoading(true);
    Promise.all([
      deviceApi.getCompatibleSoftware(hardwareId, 'DISPLAY'),
      deviceApi.getCompatibleSoftware(hardwareId, 'DEVICE'),
    ])
      .then(([display, firmware]) => {
        if (!cancelled) {
          setDisplaySoftware(display);
          setDeviceSoftware(firmware);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDisplaySoftware([]);
          setDeviceSoftware([]);
        }
      })
      .finally(() => { if (!cancelled) setSoftwareLoading(false); });

    return () => { cancelled = true; };
  }, [hardwareId]);

  return {
    hardwareList,
    displaySoftware,
    deviceSoftware,
    hardwareLoading,
    softwareLoading,
  };
}

/** Resolve stored device IDs using dropdown APIs only (for edit pre-fill) */
export async function resolveVersionIdsFromDropdowns(device) {
  const hardwareList = await deviceApi.getHardwareDropdown('device');
  const hardware_id = pickDropdownId(hardwareList, device?.hardware_id);

  if (!hardware_id) {
    return { hardware_id: '', display_software_id: '', device_software_id: '' };
  }

  const [displayList, firmwareList] = await Promise.all([
    deviceApi.getCompatibleSoftware(hardware_id, 'DISPLAY'),
    deviceApi.getCompatibleSoftware(hardware_id, 'DEVICE'),
  ]);

  return {
    hardware_id,
    display_software_id: pickDropdownId(displayList, device?.display_software_id),
    device_software_id: pickDropdownId(firmwareList, device?.device_software_id),
  };
}
